import React, { Fragment, useState, memo } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { AnyObjectSchema } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider, SubmitHandler } from 'react-hook-form';
import { Wizard } from 'react-use-wizard';
import { WizardHeader } from './WizardHeader';
import { WizardFooter } from './WizardFooter';
import { WizardWrapper } from './WizardWrapper';
import { Step } from './types';
import { getProductValidationValues } from 'user/selectors';
import { ProductValidationValues } from 'store/types/types';
import { getCurrency } from 'user/userSelectors';

type WizardSystemProps = {
  steps: Step[];
  wizardTitle?: string;
  testId?: string;
  onClose: () => void;
  onSubmit?: SubmitHandler<unknown>;
  schema: (
    step: number,
    options: { productValidationValues?: ProductValidationValues; currency: string }
  ) => AnyObjectSchema;
  defaultValues: unknown;
  className?: string;
  lastStepActions?: React.ReactNode[];
  disableSaveButton?: boolean;
};

export const WizardSystem = memo(function WizardSystem({
  steps,
  wizardTitle,
  testId = 'wizard-system',
  onClose,
  onSubmit,
  schema,
  defaultValues,
  className = '',
  lastStepActions,
  disableSaveButton,
}: WizardSystemProps) {
  const [activeStep, setActiveStep] = useState(1);
  const baseString = 'wizard';
  const productValidationValues = useSelector(getProductValidationValues);
  const currency = useSelector(getCurrency);
  const classString = classNames(`${baseString}`);
  const methods = useForm({
    resolver: yupResolver(schema(activeStep, { productValidationValues, currency })),
    mode: 'onChange',
    defaultValues,
  });

  return (
    <section className={classString} data-test-id={testId}>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          className={classNames(className, `${baseString}-form`)}
        >
          <Wizard
            header={
              <WizardHeader
                testId={testId}
                steps={steps}
                wizardTitle={wizardTitle}
                onClose={onClose}
              />
            }
            footer={
              <WizardFooter
                testId={testId}
                setActiveStep={setActiveStep}
                lastStepActions={lastStepActions}
                disableSaveButton={disableSaveButton}
              />
            }
            wrapper={<WizardWrapper testId={testId} />}
          >
            {steps.map(({ component, title }) => (
              <Fragment key={title}>{component}</Fragment>
            ))}
          </Wizard>
        </form>
      </FormProvider>
    </section>
  );
});
